import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { VerdictBadge } from '@/components/atoms/VerdictBadge';

import type { EvaluationsTableProps } from './EvaluationsTable.types';

const formatRunAt = (iso: string): string => {
	const d = new Date(iso);

	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const reasoningOf = (run: EvaluationsTableProps['rows'][number]): string => {
	const v = run.verdict;

	if (v.kind === 'rubric') return v.reasoning;
	if (v.kind === 'external_evaluation') return v.remark ?? '';

	const first = v.results.find((r) => !r.passed) ?? v.results[0];

	return first ? `${first.criterionLabel}: ${first.reasoning}` : '';
};

export const EvaluationsTable = ({ rows }: EvaluationsTableProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'evaluations' });

	return (
		<div className="overflow-x-auto rounded-md border border-subtle bg-white">
			<table className="min-w-full text-left text-[13px]">
				<thead className="border-b border-subtle bg-grey-25 text-[11px] font-medium uppercase tracking-wider text-subtle">
					<tr>
						<th className="px-md py-xs">{t('COL_THREAD')}</th>
						<th className="px-md py-xs">{t('COL_JUDGE')}</th>
						<th className="px-md py-xs">{t('COL_VERDICT')}</th>
						<th className="px-md py-xs">{t('COL_REASONING')}</th>
						<th className="px-md py-xs">{t('COL_WHEN')}</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr
							key={`${row.threadId}:${row.verdict.judgeId}:${row.verdict.runAt}`}
							className="border-b border-subtle last:border-0"
						>
							<td className="px-md py-xs">
								<Link
									to={`/conversations/${row.threadId}`}
									className="font-mono text-[12px] text-default hover:text-purple-700"
								>
									{row.threadId}
								</Link>
								<p className="text-[11px] text-subtle">{row.userName}</p>
							</td>
							<td className="px-md py-xs">
								<p className="font-medium text-default">{row.verdict.judgeName}</p>
								<p className="text-[11px] uppercase tracking-wider text-subtle">{row.verdict.kind}</p>
							</td>
							<td className="px-md py-xs">
								<VerdictBadge verdict={row.verdict} />
							</td>
							<td className="max-w-[420px] px-md py-xs text-[12px] text-subtle">{reasoningOf(row)}</td>
							<td className="px-md py-xs text-[12px] tabular-nums text-subtle">{formatRunAt(row.verdict.runAt)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};
